import { useState } from 'react';
import { FolderOpen, Link2, Copy, CloudOff } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import {
  Button, Card, CardHeader, EmptyState, Field, LoadingBlock, Modal,
  PageHeader, Select, Tabs, useToast,
} from '@/components/ui';
import { DocumentList, PhotoGrid, FileUploader } from '@/components/Files';
import type { StoredFile, Property, Tenant, FileKind } from '@shared/types';

const KIND_TABS: { id: string; label: string; kinds: FileKind[] }[] = [
  { id: 'photos', label: 'Photos', kinds: ['property_photo', 'room_photo'] },
  { id: 'contracts', label: 'Contracts', kinds: ['contract'] },
  { id: 'ids', label: 'ID documents', kinds: ['id_document'] },
  { id: 'proofs', label: 'Payment proofs', kinds: ['payment_proof'] },
  { id: 'bills', label: 'Bill documents', kinds: ['bill_document'] },
  { id: 'other', label: 'Other', kinds: ['notice', 'inspection', 'maintenance_photo', 'other'] },
];

export default function Documents() {
  const { can } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('photos');
  const [propertyId, setPropertyId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [sharing, setSharing] = useState<StoredFile | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareDays, setShareDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const { data: config } = useApi<{ configured: boolean }>('/files/config');
  const { data: properties } = useApi<Property[]>('/properties');
  const { data: tenants } = useApi<Tenant[]>('/tenants');
  const { data, loading, reload } = useApi<StoredFile[]>('/files', {
    property_id: propertyId || undefined,
    tenant_id: tenantId || undefined,
  });

  const activeTab = KIND_TABS.find((t) => t.id === tab)!;
  const files = (data ?? []).filter((f) => activeTab.kinds.includes(f.kind));
  const isPhotoTab = tab === 'photos';

  async function createShareLink() {
    if (!sharing) return;
    setBusy(true);
    try {
      const res = await api.post<{ url: string }>(`/files/${sharing.id}/share-link`, {
        expires_days: shareDays,
      });
      setShareUrl(res.url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the link');
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the link and copy it manually');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        subtitle="Every photo, contract, ID and receipt, indexed and viewable here"
      />

      {config && !config.configured && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-warn/10 border border-warn/25">
          <CloudOff className="h-5 w-5 text-warn shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-ink">Document storage is not enabled</p>
            <p className="text-muted mt-1 leading-relaxed">
              Uploads are turned off on this account. Everything else works normally —
              get in touch and we will enable it for you.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All tenants</option>
          {(tenants ?? []).map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
        </Select>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={KIND_TABS.map((t) => ({
          id: t.id,
          label: t.label,
          count: (data ?? []).filter((f) => t.kinds.includes(f.kind)).length,
        }))}
      />

      <Card>
        <CardHeader
          title={activeTab.label}
          subtitle={
            isPhotoTab
              ? 'Photos marked visible appear in the tenant portal'
              : 'Share links let a tenant open a document straight from an SMS'
          }
        />
        <div className="p-5 sm:p-6 pt-4 space-y-5">
          {can('staff') && !isPhotoTab && (
            <FileUploader
              disabled={!config?.configured}
              disabledReason="Document storage is not enabled on this account"
              label="Upload a document"
              onUpload={async (payload) => {
                await api.post('/files', {
                  ...payload,
                  kind: tab === 'contracts' ? 'contract' : tab === 'ids' ? 'id_document' : 'other',
                  property_id: propertyId || null,
                  tenant_id: tenantId || null,
                });
                reload();
              }}
            />
          )}

          {loading ? (
            <LoadingBlock rows={4} />
          ) : files.length === 0 ? (
            <EmptyState
              icon={<FolderOpen className="h-6 w-6" />}
              title={`No ${activeTab.label.toLowerCase()} yet`}
              message="Files uploaded against a property, room or tenant appear here."
            />
          ) : isPhotoTab ? (
            <PhotoGrid files={files} onChange={reload} readOnly={!can('staff')} />
          ) : (
            <DocumentList
              files={files}
              onChange={reload}
              readOnly={!can('manager')}
              onShare={(f) => { setSharing(f); setShareUrl(null); }}
            />
          )}
        </div>
      </Card>

      <Modal
        open={!!sharing}
        onClose={() => { setSharing(null); setShareUrl(null); }}
        title="Create a share link"
        subtitle={sharing?.name}
        footer={
          shareUrl ? (
            <>
              <Button onClick={() => { setSharing(null); setShareUrl(null); }}>Done</Button>
              <Button variant="primary" onClick={() => void copyLink()} icon={<Copy className="h-4 w-4" />}>
                Copy link
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setSharing(null)}>Cancel</Button>
              <Button variant="primary" onClick={() => void createShareLink()} loading={busy}
                icon={<Link2 className="h-4 w-4" />}>
                Create link
              </Button>
            </>
          )
        }
      >
        {shareUrl ? (
          <div className="space-y-3">
            <p className="text-sm text-muted">
              Anyone with this link can view the document until it expires. Paste it into an SMS
              using the <code className="text-xs bg-raised px-1 py-0.5 rounded">{'{{link}}'}</code> placeholder.
            </p>
            <div className="p-3 rounded-xl bg-raised border border-line">
              <p className="text-sm font-mono break-all text-brand">{shareUrl}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Creates a link that works without signing in, so a tenant can open the document
              directly from a text message.
            </p>
            <Field label="Expires after" hint="Shorter is safer for anything sensitive">
              <Select value={String(shareDays)} onChange={(e) => setShareDays(Number(e.target.value))}>
                <option value="1">1 day</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </Select>
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}
